import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BaseService, NO_BLOCK, RequestOptions } from './base.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { LoginResult } from '../models/login-result';
import { LocalStorageService } from './local-storage.service';
import { UserRoleEnum } from '../enums/userRoleEnum';
import { ReturnFlag } from '../models/return-flag';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { BaseServiceConfig, BASE_SERVICE_CONFIG_INJECTION_TOKEN } from './base.service.config';
import { Subscription } from 'rxjs';
import { interval } from 'rxjs';
import { NavigationService, ROUTE_METADATA } from '../core/navigation/services/navigation.service';
import { MsalService } from '@azure/msal-angular';
import { LoginLogoutService } from './login.service';
import { jwtDecode } from 'jwt-decode';
import { CryptoRandomFloat } from '../common/crypto-random-float';
import { LogoutWebCaches, WebCaches } from '../enums/webCachesEnum';
import { urlWithoutLogin } from './../app-routing.module';
import { TwoFaSetupDto } from '../models/two-fa-setup-dto';
import { LoginVerifyDto } from '../models/login-verify-dto';

@Injectable({
	providedIn: "root",
})
export class AuthService {
	constructor(
		private baseService: BaseService,
		private router: Router,
		private localStorageService: LocalStorageService,
		private http: HttpClient,
		private _loginLogoutService: LoginLogoutService,
		private _navigationService: NavigationService,
		private msalService: MsalService,
		@Inject(BASE_SERVICE_CONFIG_INJECTION_TOKEN) private config: BaseServiceConfig
	) {
		this.ApiApplicationSettings();
	}

	private static readonly REQUEST_TOP_PATH: string = "login";

	private _refresh$: Observable<LoginResult> | null = null;
	public get refresh$(): Observable<LoginResult> | null {
		return this._refresh$;
	}

	public refresh(): Observable<LoginResult> {
		if (!this._refresh$) {
			const requestEndpoint: string = "Refresh";
			let url = `${AuthService.REQUEST_TOP_PATH}/${requestEndpoint}`;

			this._refresh$ = this.baseService.get<LoginResult>(
				url,
				{
					requiresAuthentication: false,
					context: new HttpContext().set(NO_BLOCK, true),
					withCredentials: true,
				},
			).pipe(
				tap(
					(loginResult: LoginResult) => {
						this.baseService.authHeaders = new HttpHeaders(
							{
								Authorization: `Bearer ${loginResult.token}`,
							},
						);
					},
				),
				shareReplay(1),
				finalize(
					() => {
						this._refresh$ = null;
					},
				),
			);
		}

		return this._refresh$;
	}

	public get isTokenExpired(): boolean {
		// Retrieve the Authorization header, expecting the format "Bearer <token>".
		const authHeader: string | null = this.baseService.authHeaders.get('Authorization');

		if (authHeader) {
			const parts = authHeader.split(' ');

			if (parts.length === 2 && parts[0] === 'Bearer') {
				const token = parts[1];

				try {
					const decodedToken: { exp: number } = jwtDecode(token);
					const currentTime = Date.now() / 1000; // Convert to seconds.

					return decodedToken.exp < currentTime;
				} catch (error) {
					return true; // Consider the token expired in case of an error.
				}
			}
		}

		return true; // Assume expired if no token or incorrect format is found.
	}

	setup2fa(login: string): Observable<TwoFaSetupDto> {
		return this.baseService.post("TwoFactorAuth/Setup2Fa", { login: login });
	}

	verify2fa(dto: LoginVerifyDto): Observable<LoginResult> {
		return this.baseService.post("TwoFactorAuth/Verify2Fa", dto);
	}

	verify2FaSetup(dto: LoginVerifyDto): Observable<void> {
		return this.baseService.post("TwoFactorAuth/VerifySetup", dto);
	}

	logIn(login: string, password: string, visitorId: string, loginInfo: number): Observable<LoginResult> {
		return this.baseService.post("login/login", { login: login, password: password, visitorId: visitorId, Action: loginInfo }).pipe(tap((data: LoginResult) => this.loginInternal(data)));
	}

	private loginInternal(data: LoginResult) {
		if (data.flag > 0) {
			this.localStorageService.set(LoginResult.storage_key, data);
			this.isAdminSubject.next(this.currentRole === UserRoleEnum.Admin);
		}
	}

	public getPermissions(requestOptions?: RequestOptions): Observable<RolePermission[]> {
		const requestEndpoint: string = "GetRolePermissions";

		let url = `settings/${requestEndpoint}`;

		return this.baseService.get(
			url,
			requestOptions
		);
	}

	public logOut() {
		this.localStorageService.remove(LoginResult.storage_key);
		this.localStorageService.remove(RolePermission.storage_key);
		this.localStorageService.remove("applicationColor");
		this.localStorageService.remove("applicationVersion");
		this.localStorageService.remove("applicationTitle");
		this.localStorageService.remove("RolePermission");
		this.localStorageService.remove("_coreSettingTypesMap");
		this.localStorageService.remove("UserInfo");
		window.localStorage.setItem('logout-event', CryptoRandomFloat.getCryptoRandomFloat().toString());
		//this.ClearLocalStorageItems();
		this.LogoutWebCaches();

		if (this.msalService && this.msalService.instance && this.msalService.instance.getAllAccounts()
			&& localStorage.getItem("LoginType") && localStorage.getItem("LoginType") == "3") {

			this.msalService.logout();

			this.msalService["browserStorage"].clear();
		}
		this.localStorageService.removeWithPrefix('perito_bookmarks');
		//this._navigationService.navigate(ROUTE_METADATA.Login);
		window.location.reload();
		//this._loginLogoutService.logOut()
		//return of();
	}

	ClearLocalStorageItems(): void {
		// Loop through all the keys of the enum
		for (const key of Object.keys(WebCaches)) {
			const itemKey = WebCaches[key as keyof typeof WebCaches];
			// Remove the item from localStorage
			localStorage.removeItem(itemKey);
		}
	}

	LogoutWebCaches(): void {
		// Loop through all the keys of the enum
		for (const key of Object.keys(LogoutWebCaches)) {
			const itemKey = LogoutWebCaches[key as keyof typeof LogoutWebCaches];
			// Remove the item from localStorage
			localStorage.removeItem(itemKey);
		}
	}

	WebMulSettings(action: string): Observable<any> {
		let url = 'login/WebMulSettings';
		return this.baseService.post(url);
	}

	WebLinkNotIn(url: string): boolean {
		let containsValue = urlWithoutLogin.includes(url);
		return containsValue ?? false;
	}

	ValuesSet: false
	private ApplicationColor = new BehaviorSubject<string>('#ff5722');
	private ApplicationVersion = new BehaviorSubject<string>('x.0.0');
	private ApplicationTitle = new BehaviorSubject<string>('Perito IBP');

	castApplicationColor = this.ApplicationColor.asObservable();
	castApplicationVersion = this.ApplicationVersion.asObservable();
	castApplicationTitle = this.ApplicationTitle.asObservable();

	ApiApplicationSettings() {
		if (this.localStorageService.get("applicationColor")) {
			this.ApplicationColor.next(this.localStorageService.get("applicationColor"));
			this.ApplicationVersion.next(this.localStorageService.get("applicationVersion"));
			this.ApplicationTitle.next(this.localStorageService.get("applicationTitle"));
		}
		else if (this.isLoggedIn && !this.ValuesSet) {
			let url = 'Settings/WebMulSettings?action=ApplicationSettings';
			this.baseService.post(url).subscribe(
				(response) => {
					if (response != null) {
						let result = response[0];
						if (result != null && result["applicationColor"] != null && result["applicationVersion"] != null && result["applicationTitle"] != null) {
							this.localStorageService.set("applicationColor", result["applicationColor"]);
							this.localStorageService.set("applicationVersion", result["applicationVersion"]);
							this.localStorageService.set("applicationTitle", result["applicationTitle"]);
							this.ApplicationColor.next(result["applicationColor"]);
							this.ApplicationVersion.next(result["applicationVersion"]);
							this.ApplicationTitle.next(result["applicationTitle"]);
						}
					}
				},
				(error) => {
					console.error("ApiApplicationSettings Error: " + error);
				});
		}
	}
	get isLoggedIn(): boolean {
		let data = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		let permissions = this.localStorageService.get<RolePermission[]>(RolePermission.storage_key);
		return data !== null && data !== undefined && new Date(data.expireDate) > new Date() && permissions && permissions.length > 0;
	}

	get isLoggedExists(): boolean {
		let data = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		if (data !== null && data !== undefined) {
			return true;
		}
		return false;
	}
	
	get currentUserName(): string {
		const data = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		if (data && data.userName) {
			return data.userName;
		}
		return null;
	}

	get currentRole(): UserRoleEnum {
		const data = this.localStorageService.get<LoginResult>(LoginResult.storage_key);
		return data?.roleId;
	}

	forgotPassword(loginid: string): Observable<ReturnFlag> {
		return this.baseService.post("login/forgotPassword", { loginid: loginid });
	}

	forgotLinkPassword(loginid: string): Observable<ReturnFlag> {
		return this.getHttpPostData("login/forgotLinkPassword", { loginid: loginid });
	}

	getHttpPostData(url, bodyData): Observable<any> {
		let options = this.baseService.getOptionsWithoptions();
		return this.http.post<any>(this.getCorrectUrl(url), bodyData, { ...options });
	}


	getSingleSingOnOption(): Observable<ReturnFlag> {
		const cachedData = localStorage.getItem(WebCaches.EnableSingleLogon);
		if (cachedData) {
			return of(JSON.parse(cachedData));
		}
		return this.getHttpPostData("login/SelectEnableSingleLogon", {}).pipe(
			tap(data => {
				localStorage.setItem(WebCaches.EnableSingleLogon, JSON.stringify(data));
			}));
	}

	getCorrectUrl(url: string): string {
		if (url.indexOf('api') === -1) {
			url = '/api' + (url[0] === '/' ? '' : '/') + url;
		}
		return encodeURI(this.config.apiEndpoint + (url[0] === '/' ? '' : '/') + url);
	}

	public ValidateLoginSession(): Observable<ReturnFlag> {
		let url = `user/ValidateLoginSession`;
		return this.baseService.post(url);
	}

	subscription: Subscription;
	statusText: string;

	public ValidateUserLoginSession() {
		this.getSingleSingOnOption().subscribe((result) => {
			if (result.msg == 'yes' && this.isLoggedIn) {

				this.subscription = interval(10000).subscribe((x => {
					if (this.isLoggedIn) {

						this.ValidateLoginSession().subscribe(
							(ValidUser) => {
								if (ValidUser.flag <= 0) {
									this.logOut();
								}
							}
						);
					}

				}));
			}
		});
	}

	ValidateUserLoginSessionUnsubscribe() {
		if (this.subscription != null && this.subscription != undefined) {
			this.subscription.unsubscribe();
		}
	}

	ADMSLogin(data: any): Observable<any> {
		return this.baseService.post("login/ADMSLogin", data).pipe(tap((data: LoginResult) => this.loginInternal(data)));
	}

	private isAdminSubject = new BehaviorSubject<boolean>((this.currentRole ?? 0) === UserRoleEnum.Admin);
	public isAdmin$: Observable<boolean> = this.isAdminSubject.asObservable();

	public get isAdmin(): boolean {
		return this.isAdminSubject.getValue();
	}

}

export class RolePermission {
	static readonly storage_key = 'RolePermission';
	menuId: number;
	menuName: string;
	isParent: number;
	menuIcon: string;
	mOrder: number;
	isVisible: boolean;
}

export class TApplicationSetting {
	ApplicationColor: string;
	ApplicationVersion: string;
	ApplicationTitle: string;
	ValuesSet: boolean;
}
